import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Package, Mail, Phone, MapPin, Clock, CheckCircle, XCircle, AlertCircle, Plus, Pencil, Trash2, Save, X, FileText, Settings, ArrowLeft, Eye, EyeOff, Globe, ExternalLink, RefreshCw, Image } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_OPTIONS = ['Package', 'Truck', 'Trash2', 'Home', 'Box', 'ShoppingCart', 'Wrench', 'MapPin'];

// --- Reusable Admin Input ---
const AdminInput = ({ label, value, onChange, placeholder, type = 'text', required = false, testId }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-1">{label}{required && <span className="text-red-400">*</span>}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testId}
      className="w-full px-4 py-2.5 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] focus:outline-none transition"
    />
  </div>
);

// --- Reusable Admin Textarea ---
const AdminTextarea = ({ label, value, onChange, placeholder, rows = 4, required = false, testId }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-1">{label}{required && <span className="text-red-400">*</span>}</label>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      required={required}
      data-testid={testId}
      className="w-full px-4 py-2.5 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] focus:outline-none transition resize-y"
    />
  </div>
);

// ============================================================
// BLOG MANAGEMENT TAB
// ============================================================
const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', author: 'YYC Pick-Up Team', published: true });

  const fetchBlogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blogs`);
      setBlogs(res.data);
    } catch { toast.error('Failed to load blogs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  const resetForm = () => {
    setForm({ title: '', excerpt: '', content: '', author: 'YYC Pick-Up Team', published: true });
    setEditingBlog(null);
    setShowForm(false);
  };

  const openEditForm = (blog) => {
    setForm({ title: blog.title, excerpt: blog.excerpt, content: blog.content, author: blog.author, published: blog.published });
    setEditingBlog(blog);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingBlog) {
        await axios.patch(`${API}/blogs/${editingBlog.id}`, { title: form.title, content: form.content, excerpt: form.excerpt, published: form.published });
        toast.success('Blog updated!');
      } else {
        await axios.post(`${API}/blogs`, form);
        toast.success('Blog created!');
      }
      resetForm();
      fetchBlogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save blog');
    } finally { setSaving(false); }
  };

  const handleDelete = async (blogId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/blogs/${blogId}`);
      toast.success('Blog deleted');
      fetchBlogs();
    } catch { toast.error('Failed to delete blog'); }
  };

  if (loading) return <div className="text-gray-400 text-center py-12">Loading blogs...</div>;

  // Blog Form
  if (showForm) {
    return (
      <div>
        <button onClick={resetForm} className="flex items-center text-[#d4af37] hover:text-[#b8941f] mb-6 transition" data-testid="blog-form-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog List
        </button>
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
          <h3 className="text-xl font-bold text-white mb-6">{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</h3>
          <form onSubmit={handleSubmit} className="space-y-5">
            <AdminInput label="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Blog post title" required testId="blog-title-input" />
            <AdminInput label="Author" value={form.author} onChange={e => setForm({...form, author: e.target.value})} placeholder="Author name" testId="blog-author-input" />
            <AdminTextarea label="Excerpt" value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} placeholder="Short description shown in blog listings" rows={2} required testId="blog-excerpt-input" />
            <AdminTextarea label="Content (Markdown supported)" value={form.content} onChange={e => setForm({...form, content: e.target.value})} placeholder="Write your blog post content here. You can use Markdown formatting (## headings, **bold**, - lists, etc.)" rows={14} required testId="blog-content-input" />
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setForm({...form, published: !form.published})} className="flex items-center gap-2 text-sm" data-testid="blog-published-toggle">
                {form.published ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-gray-500" />}
                <span className={form.published ? 'text-green-400' : 'text-gray-500'}>{form.published ? 'Published' : 'Draft'}</span>
              </button>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="flex items-center gap-2 bg-[#d4af37] text-black px-6 py-2.5 rounded-lg font-semibold hover:bg-[#b8941f] transition disabled:opacity-50" data-testid="blog-save-btn">
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : (editingBlog ? 'Update Blog' : 'Create Blog')}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg font-semibold border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Blog List
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-400">{blogs.length} blog post{blogs.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[#d4af37] text-black px-5 py-2.5 rounded-lg font-semibold hover:bg-[#b8941f] transition" data-testid="create-blog-btn">
          <Plus className="w-4 h-4" /> New Blog Post
        </button>
      </div>
      {blogs.length === 0 ? (
        <div className="bg-[#1a1a1a] rounded-xl p-8 text-center border border-gray-800">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No blog posts yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {blogs.map(blog => (
            <div key={blog.id} className="bg-[#1a1a1a] rounded-xl p-5 border border-gray-800 hover:border-gray-700 transition" data-testid={`blog-item-${blog.id}`}>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-lg font-bold text-white truncate">{blog.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${blog.published ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {blog.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm truncate">{blog.excerpt}</p>
                  <p className="text-gray-600 text-xs mt-1">By {blog.author} &middot; {new Date(blog.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => openEditForm(blog)} className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-[#d4af37] transition" title="Edit" data-testid={`edit-blog-${blog.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(blog.id, blog.title)} className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-red-400 transition" title="Delete" data-testid={`delete-blog-${blog.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// SERVICE MANAGEMENT TAB
// ============================================================
const ServiceManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ id: '', name: '', description: '', icon: 'Package', features: [''] });

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch { toast.error('Failed to load services'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const resetForm = () => {
    setForm({ id: '', name: '', description: '', icon: 'Package', features: [''] });
    setEditingService(null);
    setShowForm(false);
  };

  const openEditForm = (svc) => {
    setForm({ id: svc.id, name: svc.name, description: svc.description, icon: svc.icon, features: svc.features.length > 0 ? [...svc.features] : [''] });
    setEditingService(svc);
    setShowForm(true);
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const updateFeature = (idx, val) => {
    const updated = [...form.features];
    updated[idx] = val;
    setForm({...form, features: updated});
  };

  const addFeature = () => setForm({...form, features: [...form.features, '']});

  const removeFeature = (idx) => {
    if (form.features.length <= 1) return;
    setForm({...form, features: form.features.filter((_, i) => i !== idx)});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanFeatures = form.features.filter(f => f.trim());
    if (cleanFeatures.length === 0) { toast.error('Add at least one feature'); return; }
    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, { name: form.name, description: form.description, icon: form.icon, features: cleanFeatures });
        toast.success('Service updated!');
      } else {
        const slug = form.id.trim() || generateSlug(form.name);
        await axios.post(`${API}/services/create`, { id: slug, name: form.name, description: form.description, icon: form.icon, features: cleanFeatures });
        toast.success('Service created!');
      }
      resetForm();
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save service');
    } finally { setSaving(false); }
  };

  const handleDelete = async (serviceId, name) => {
    if (!window.confirm(`Delete service "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      toast.success('Service deleted');
      fetchServices();
    } catch { toast.error('Failed to delete service'); }
  };

  if (loading) return <div className="text-gray-400 text-center py-12">Loading services...</div>;

  // Service Form
  if (showForm) {
    return (
      <div>
        <button onClick={resetForm} className="flex items-center text-[#d4af37] hover:text-[#b8941f] mb-6 transition" data-testid="service-form-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Services List
        </button>
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
          <h3 className="text-xl font-bold text-white mb-6">{editingService ? 'Edit Service' : 'Create New Service'}</h3>
          <form onSubmit={handleSubmit} className="space-y-5">
            <AdminInput label="Service Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Junk Removal" required testId="service-name-input" />
            {!editingService && (
              <AdminInput label="URL Slug (auto-generated if empty)" value={form.id} onChange={e => setForm({...form, id: e.target.value})} placeholder={form.name ? generateSlug(form.name) : 'e.g. junk-removal'} testId="service-id-input" />
            )}
            <AdminTextarea label="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe this service for customers" rows={3} required testId="service-desc-input" />
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Icon</label>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map(icon => (
                  <button key={icon} type="button" onClick={() => setForm({...form, icon})} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${form.icon === icon ? 'bg-[#d4af37] text-black' : 'bg-black border border-gray-700 text-gray-400 hover:border-gray-500'}`} data-testid={`icon-${icon}`}>
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Features / Offerings</label>
              <div className="space-y-2">
                {form.features.map((feat, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      value={feat}
                      onChange={e => updateFeature(idx, e.target.value)}
                      placeholder={`Feature ${idx + 1}`}
                      className="flex-1 px-4 py-2 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#d4af37] focus:outline-none transition"
                      data-testid={`feature-input-${idx}`}
                    />
                    {form.features.length > 1 && (
                      <button type="button" onClick={() => removeFeature(idx)} className="p-2 text-gray-500 hover:text-red-400 transition" data-testid={`remove-feature-${idx}`}>
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={addFeature} className="mt-2 flex items-center gap-1 text-sm text-[#d4af37] hover:text-[#b8941f] transition" data-testid="add-feature-btn">
                <Plus className="w-3 h-3" /> Add Feature
              </button>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="flex items-center gap-2 bg-[#d4af37] text-black px-6 py-2.5 rounded-lg font-semibold hover:bg-[#b8941f] transition disabled:opacity-50" data-testid="service-save-btn">
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : (editingService ? 'Update Service' : 'Create Service')}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg font-semibold border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Service List
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-400">{services.length} service{services.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[#d4af37] text-black px-5 py-2.5 rounded-lg font-semibold hover:bg-[#b8941f] transition" data-testid="create-service-btn">
          <Plus className="w-4 h-4" /> New Service
        </button>
      </div>
      {services.length === 0 ? (
        <div className="bg-[#1a1a1a] rounded-xl p-8 text-center border border-gray-800">
          <Settings className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No services yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map(svc => (
            <div key={svc.id} className="bg-[#1a1a1a] rounded-xl p-5 border border-gray-800 hover:border-gray-700 transition" data-testid={`service-item-${svc.id}`}>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-bold text-white">{svc.name}</h4>
                  <p className="text-gray-500 text-sm mt-1 line-clamp-2">{svc.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {svc.features.slice(0, 3).map((f, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400">{f}</span>
                    ))}
                    {svc.features.length > 3 && <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-500">+{svc.features.length - 3} more</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => openEditForm(svc)} className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-[#d4af37] transition" title="Edit" data-testid={`edit-service-${svc.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(svc.id, svc.name)} className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-red-400 transition" title="Delete" data-testid={`delete-service-${svc.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// BOOKINGS TAB
// ============================================================
const BookingsManager = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}?status=${status}`);
      toast.success('Booking status updated');
      fetchBookings();
    } catch { toast.error('Failed to update booking'); }
  };

  const filtered = filterStatus ? bookings.filter(b => b.status === filterStatus) : bookings;

  if (loading) return <div className="text-gray-400 text-center py-12">Loading bookings...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-400">{bookings.length} total booking{bookings.length !== 1 ? 's' : ''}</p>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-4 py-2 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white text-sm" data-testid="filter-status">
          <option value="">All Bookings</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <div className="space-y-3" data-testid="bookings-list">
        {filtered.length === 0 ? (
          <div className="bg-[#1a1a1a] rounded-xl p-8 text-center border border-gray-800">
            <p className="text-gray-400">No bookings found</p>
          </div>
        ) : filtered.map(booking => (
          <div key={booking.id} className="bg-[#1a1a1a] rounded-xl p-5 border border-gray-800 hover:border-gray-700 transition" data-testid={`booking-${booking.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="text-lg font-bold text-white">{booking.name}</h4>
                <p className="text-[#d4af37] text-sm font-semibold">{booking.service}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                booking.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>{booking.status}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-400 mb-3">
              <div className="flex items-center"><Mail className="w-3.5 h-3.5 mr-2 text-[#d4af37]" />{booking.email}</div>
              <div className="flex items-center"><Phone className="w-3.5 h-3.5 mr-2 text-[#d4af37]" />{booking.phone}</div>
              <div className="flex items-center"><Calendar className="w-3.5 h-3.5 mr-2 text-[#d4af37]" />{booking.preferred_date}</div>
              <div className="flex items-center"><Clock className="w-3.5 h-3.5 mr-2 text-[#d4af37]" />{booking.preferred_time}</div>
              <div className="flex items-start sm:col-span-2"><MapPin className="w-3.5 h-3.5 mr-2 text-[#d4af37] mt-0.5 flex-shrink-0" />{booking.address}</div>
            </div>
            {booking.description && (
              <div className="p-3 bg-black/50 rounded-lg border border-gray-800 mb-3">
                <p className="text-xs text-gray-400"><strong className="text-white">Details:</strong> {booking.description}</p>
              </div>
            )}
            <div className="flex items-center justify-between pt-3 border-t border-gray-800">
              <p className="text-xs text-gray-500">Submitted: {new Date(booking.created_at).toLocaleString()}</p>
              {booking.status === 'pending' && (
                <div className="flex gap-2">
                  <button onClick={() => updateStatus(booking.id, 'completed')} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-xs font-semibold transition" data-testid="complete-booking">
                    Complete
                  </button>
                  <button onClick={() => updateStatus(booking.id, 'cancelled')} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-xs font-semibold transition" data-testid="cancel-booking">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// MESSAGES TAB
// ============================================================
const MessagesManager = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contact`);
      setMessages(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch { toast.error('Failed to load messages'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  if (loading) return <div className="text-gray-400 text-center py-12">Loading messages...</div>;

  return (
    <div>
      <div className="mb-6">
        <p className="text-gray-400">{messages.length} message{messages.length !== 1 ? 's' : ''}</p>
      </div>
      <div className="space-y-3" data-testid="messages-list">
        {messages.length === 0 ? (
          <div className="bg-[#1a1a1a] rounded-xl p-8 text-center border border-gray-800">
            <p className="text-gray-400">No messages found</p>
          </div>
        ) : messages.map(msg => (
          <div key={msg.id} className="bg-[#1a1a1a] rounded-xl p-5 border border-gray-800 hover:border-gray-700 transition" data-testid={`message-${msg.id}`}>
            <div className="mb-3">
              <h4 className="text-lg font-bold text-white mb-1">{msg.name}</h4>
              <div className="flex flex-col sm:flex-row sm:gap-6 gap-1 text-sm">
                <a href={`mailto:${msg.email}`} className="flex items-center text-gray-400 hover:text-[#d4af37] transition">
                  <Mail className="w-3.5 h-3.5 mr-2 text-[#d4af37]" />{msg.email}
                </a>
                <a href={`tel:${msg.phone}`} className="flex items-center text-gray-400 hover:text-[#d4af37] transition">
                  <Phone className="w-3.5 h-3.5 mr-2 text-[#d4af37]" />{msg.phone}
                </a>
              </div>
            </div>
            <div className="p-3 bg-black/50 rounded-lg border border-gray-800 mb-3">
              <p className="text-gray-300 text-sm">{msg.message}</p>
            </div>
            <p className="text-xs text-gray-500">Received: {new Date(msg.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// SITE SETTINGS TAB
// ============================================================
const SiteSettingsManager = () => {
  const [settings, setSettings] = useState({ og_title: '', og_description: '', og_image_url: '', site_url: 'https://yycpickupdelivery.ca' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch { /* use defaults */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-settings`, settings);
      toast.success('Settings saved! Deploy your site and refresh Facebook cache to see changes.');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const openFbDebugger = () => {
    window.open(`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(settings.site_url)}`, '_blank');
  };

  const openLinkedInInspector = () => {
    window.open(`https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(settings.site_url)}`, '_blank');
  };

  if (loading) return <div className="text-gray-400 text-center py-12">Loading settings...</div>;

  return (
    <div className="space-y-6">
      {/* Social Preview Card */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#d4af37]" /> Social Sharing Preview
        </h3>
        <p className="text-gray-400 text-sm mb-5">This is how your link appears when shared on Facebook, Twitter, LinkedIn, etc.</p>

        {/* Preview mockup */}
        <div className="bg-[#242526] rounded-lg overflow-hidden max-w-lg border border-gray-700 mb-6">
          <div className="aspect-[1200/630] bg-black flex items-center justify-center">
            <img
              src={settings.og_image_url || `${BACKEND_URL}/og-image.png`}
              alt="OG Preview"
              className="w-full h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
              data-testid="og-preview-image"
            />
          </div>
          <div className="p-3">
            <p className="text-xs text-gray-500 uppercase">{settings.site_url.replace('https://', '')}</p>
            <p className="text-white font-semibold text-sm mt-0.5">{settings.og_title || 'YYC Pick-Up: Delivery & Hauling Services Ltd.'}</p>
            <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{settings.og_description || 'Calgary\'s premier delivery and hauling service.'}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button onClick={openFbDebugger} className="flex items-center gap-2 bg-[#1877F2] text-white px-4 py-2.5 rounded-lg font-medium text-sm hover:bg-[#1565C0] transition" data-testid="fb-debugger-btn">
            <RefreshCw className="w-4 h-4" /> Refresh Facebook Cache
          </button>
          <button onClick={openLinkedInInspector} className="flex items-center gap-2 bg-[#0A66C2] text-white px-4 py-2.5 rounded-lg font-medium text-sm hover:bg-[#0855A5] transition" data-testid="linkedin-inspector-btn">
            <ExternalLink className="w-4 h-4" /> LinkedIn Post Inspector
          </button>
        </div>
      </div>

      {/* Edit OG Settings */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Image className="w-5 h-5 text-[#d4af37]" /> Edit Link Preview Settings
        </h3>
        <div className="space-y-4">
          <AdminInput label="Site URL" value={settings.site_url} onChange={e => setSettings({...settings, site_url: e.target.value})} placeholder="https://yycpickupdelivery.ca" testId="settings-site-url" />
          <AdminInput label="Link Title (og:title)" value={settings.og_title} onChange={e => setSettings({...settings, og_title: e.target.value})} placeholder="YYC Pick-Up: Delivery & Hauling Services Ltd." testId="settings-og-title" />
          <AdminTextarea label="Link Description (og:description)" value={settings.og_description} onChange={e => setSettings({...settings, og_description: e.target.value})} placeholder="Calgary's premier delivery and hauling service." rows={2} testId="settings-og-desc" />
          <AdminInput label="Custom OG Image URL (leave blank to use default logo)" value={settings.og_image_url} onChange={e => setSettings({...settings, og_image_url: e.target.value})} placeholder="https://example.com/your-image.png" testId="settings-og-image" />
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[#d4af37] text-black px-6 py-2.5 rounded-lg font-semibold hover:bg-[#b8941f] transition disabled:opacity-50" data-testid="settings-save-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-bold text-white mb-3">How to update your link preview</h3>
        <ol className="space-y-2 text-gray-400 text-sm list-decimal list-inside">
          <li>Update the settings above and click <strong className="text-white">Save Settings</strong></li>
          <li>Click <strong className="text-white">Deploy</strong> in the Emergent interface to push changes live</li>
          <li>Click <strong className="text-[#1877F2]">Refresh Facebook Cache</strong> above — paste your URL and click "Scrape Again"</li>
          <li>Facebook / LinkedIn will now show your updated logo and description</li>
        </ol>
        <p className="text-gray-500 text-xs mt-4">Note: Social platforms cache link previews. After deploying, you must use the debugger tools above to force them to fetch the updated image.</p>
      </div>
    </div>
  );
};

// ============================================================
// MAIN ADMIN PAGE
// ============================================================
const TABS = [
  { id: 'bookings', label: 'Bookings', icon: Calendar },
  { id: 'messages', label: 'Messages', icon: Mail },
  { id: 'blogs', label: 'Blogs', icon: FileText },
  { id: 'services', label: 'Services', icon: Settings },
  { id: 'settings', label: 'Site Settings', icon: Globe },
];

const Admin = () => {
  const [activeTab, setActiveTab] = useState('bookings');

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-[#111] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center gap-4">
            <img
              src="https://static.wixstatic.com/media/f1e95d_bd3da2d723c5414bbf05fd6cc2f28086~mv2.png"
              alt="YYC Pick-Up"
              className="h-16 w-auto"
            />
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="admin-title">Admin Dashboard</h1>
              <p className="text-gray-500 text-sm">Manage your website content, bookings, and inquiries</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-[#0a0a0a] border-b border-gray-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition ${
                    activeTab === tab.id
                      ? 'bg-[#d4af37] text-black'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'bookings' && <BookingsManager />}
        {activeTab === 'messages' && <MessagesManager />}
        {activeTab === 'blogs' && <BlogManager />}
        {activeTab === 'services' && <ServiceManager />}
        {activeTab === 'settings' && <SiteSettingsManager />}
      </div>
    </div>
  );
};

export default Admin;
